import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import {
  type AlertConfig,
  type AlertLogsResponse,
  type AlertSummaryResponse,
  getAlertConfig,
  getAlertLogs,
  getRecentAlerts,
  sendTestAlert,
  updateAlertConfig,
} from "@/api/alerts-api"

import { FAST_STALE_TIME, GC_TIME } from "./_shared"

export function useAdminAlertConfigQuery(enabled = true) {
  return useQuery<AlertConfig>({
    queryKey: ["admin", "alerts", "config"],
    queryFn: () => getAlertConfig(),
    enabled,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useUpdateAlertConfigMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (config: Partial<AlertConfig>) => {
      return await updateAlertConfig(config)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "alerts", "config"] })
    },
  })
}

export function useAdminAlertLogsQuery(
  params?: {
    start_date?: string
    end_date?: string
    alert_type?: string
    severity?: string
    channel?: string
    limit?: number
    offset?: number
  },
  enabled = true
) {
  return useQuery<AlertLogsResponse>({
    queryKey: ["admin", "alerts", "logs", params],
    queryFn: () => getAlertLogs(params),
    enabled,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useAdminRecentAlertsQuery(days?: number, enabled = true) {
  return useQuery<AlertSummaryResponse>({
    queryKey: ["admin", "alerts", "recent", days],
    queryFn: () => getRecentAlerts(days),
    enabled,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useSendTestAlertMutation() {
  return useMutation({
    mutationFn: async (params?: { severity?: "info" | "warning" | "critical" }) => {
      return await sendTestAlert(params)
    },
  })
}
